import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  CalendarDays,
  Gem,
  Package,
  ShieldCheck,
  Target,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import {
  getPearlsCommissionDetail,
  clearPearlsCommissionDetailState,
} from "../../../../Redux/referral/slice";
import {
  formatEarningsCurrency,
  normalizeEarningsStatus,
  statusLabel,
} from "../earningsAdapters";
import "./SalarPearls.css";

const statusClass = (status: string) =>
  `salar-pearls-status salar-pearls-status-${normalizeEarningsStatus(status || "")}`;

const SalarCoinsDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: string }>();
  const { data: item, loading, error } = useSelector(
    (state: RootState) => state.referral.pearlsCommissionDetail,
  );

  useEffect(() => {
    if (id) {
      dispatch(getPearlsCommissionDetail(id));
    }
    return () => {
      dispatch(clearPearlsCommissionDetailState());
    };
  }, [dispatch, id]);

  const backButton = (
    <button
      type="button"
      className="salar-pearls-back-button"
      onClick={() => navigate("/SalarCoins")}
      aria-label="Back to Salar Pearls"
    >
      <ArrowLeft size={20} />
    </button>
  );

  if (loading) {
    return (
      <section className="salar-pearls-page">
        <div className="salar-pearls-shell">
          <header className="salar-pearls-page-header salar-pearls-details-header">
            <div className="salar-pearls-title-wrap">
              {backButton}
              <div>
                <span className="salar-pearls-eyebrow">PEARLS DETAILS</span>
                <h1>Salar Pearls Details</h1>
                <p>Loading record...</p>
              </div>
            </div>
          </header>
        </div>
      </section>
    );
  }

  if (!item || error) {
    return (
      <section className="salar-pearls-page">
        <div className="salar-pearls-shell">
          <header className="salar-pearls-page-header salar-pearls-details-header">
            <div className="salar-pearls-title-wrap">
              {backButton}
              <div>
                <span className="salar-pearls-eyebrow">PEARLS DETAILS</span>
                <h1>Salar Pearls Details</h1>
                <p>Order, product, milestone and pearl reward information.</p>
              </div>
            </div>
          </header>
          <div className="salar-pearls-empty">
            <Gem size={30} />
            <strong>Record not found</strong>
            <span>{error || "This pearls record is unavailable."}</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="salar-pearls-page">
      <div className="salar-pearls-shell">
        <header className="salar-pearls-page-header salar-pearls-details-header">
          <div className="salar-pearls-title-wrap">
            {backButton}
            <div>
              <span className="salar-pearls-eyebrow">PEARLS DETAILS</span>
              <h1>Salar Pearls Details</h1>
              <p>
                Order, product, milestone and pearl reward information.
              </p>
            </div>
          </div>
          <span className={statusClass(item.salarPearlStatus)}>
            {statusLabel(item.salarPearlStatus)}
          </span>
        </header>

        <div className="salar-pearls-details-grid">
          <section className="salar-pearls-detail-card salar-pearls-detail-order">
            <div className="salar-pearls-detail-heading">
              <CalendarDays size={20} />
              <div>
                <span>ORDER INFORMATION</span>
                <h2>Purchase Record</h2>
              </div>
            </div>
            <div className="salar-pearls-detail-kv-grid">
              <div>
                <span>Order Date</span>
                <strong>{item.date}</strong>
              </div>
              <div>
                <span>Order ID</span>
                <strong>{item.orderId}</strong>
              </div>
              <div>
                <span>Achieved Date</span>
                <strong>{item.achievedDate}</strong>
              </div>
              <div>
                <span>Status</span>
                <span className={statusClass(item.status)}>
                  {statusLabel(item.status)}
                </span>
              </div>
            </div>
          </section>

          <section className="salar-pearls-detail-card">
            <div className="salar-pearls-detail-heading">
              <Package size={20} />
              <div>
                <span>PRODUCT INFORMATION</span>
                <h2>Salar Shopping Voucher</h2>
              </div>
            </div>
            <div className="salar-pearls-detail-product-row">
              <img
                src={item.productImage || "../../assets/img/women/women-4.png"}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "../../assets/img/women/women-4.png";
                }}
              />
              <div>
                <span className="salar-pearls-section-label">
                  PRODUCT NAME
                </span>
                <h3>{item.productName}</h3>
                <p>
                  Quantity:{" "}
                  <strong>{item.quantity ?? 1} Item</strong>
                </p>
                <strong className="salar-pearls-detail-price">
                  {formatEarningsCurrency(item.salarShoppingVoucher)}
                </strong>
              </div>
            </div>
          </section>

          <section className="salar-pearls-detail-card">
            <div className="salar-pearls-detail-heading">
              <Gem size={20} />
              <div>
                <span>PEARLS INFORMATION</span>
                <h2>Reward Balance</h2>
              </div>
            </div>
            <div className="salar-pearls-highlight">
              <span>SALAR PEARLS</span>
              <strong>{(item.salarPearls ?? 0).toLocaleString("en-IN")}</strong>
              <small>Pearls credited for this purchase</small>
            </div>
          </section>

          <section className="salar-pearls-detail-card">
            <div className="salar-pearls-detail-heading">
              <Target size={20} />
              <div>
                <span>MILESTONE INFORMATION</span>
                <h2>Achieved Stage</h2>
              </div>
            </div>
            <div className="salar-pearls-milestone-strip">
              <span>STAGE</span>
              <strong>{item.milestoneName}</strong>
            </div>
          </section>
        </div>

        <section className="salar-pearls-detail-footer-card">
          <ShieldCheck size={20} />
          <div>
            <strong>Pearl record protected</strong>
            <span>
              This pearl reward is linked to your Salar account and order
              records.
            </span>
          </div>
        </section>
      </div>
    </section>
  );
};

export default SalarCoinsDetails;
